import { withTransaction } from '@elastic/apm-rum-react';
import {
    Check,
    Close,
    AMBER,
    RED,
    Cached
} from '@swegon-core/uic/dist/src/theme';
import React, { useContext } from 'react';
import Working from '../common/Working';
import EventStore from '../websocket/eventStore';
import { calculationIsDisabled } from './calculation.functions';
import CalculationEventWrapper, {
    CalculationResultChangeEvent
} from './CalculationEventWrapper';
import { CalculationStatus } from './CalculationResult.types';
import { Button } from '@swegon-core/uic';
import { AHUD_PURPLE } from '../../../theme';
import { Context as TranslationContext } from '../../contexts/TranslationContext';

export interface CalculateButtonProps {
    ahuCalculationStatus: CalculationStatus;
    eventStore: EventStore | undefined;
    handleCalculationClick: () => void;
    onCalculationResultChanged: (event: CalculationResultChangeEvent) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
const CalculateButton: React.FC<CalculateButtonProps> = ({
    ahuCalculationStatus,
    eventStore,
    handleCalculationClick,
    onCalculationResultChanged
}) => {
    const { l } = useContext(TranslationContext);
    const shouldDisableButton = calculationIsDisabled(ahuCalculationStatus);

    const getColor = () => {
        switch (ahuCalculationStatus) {
            case 'CALC_RES_OK':
                return undefined;
            case 'CALC_RES_OK_WITH_WARNINGS':
                return AMBER;
            case 'CALC_RES_OK_WITH_ERRORS':
            case 'CALC_RES_ERROR':
                return RED;
            default:
                return AHUD_PURPLE;
        }
    };

    const getIcon = () => {
        switch (ahuCalculationStatus) {
            case 'CALC_NEEDS_REFRESH':
                return <Cached size="medium" />;
            case 'CALC_RUNNING':
                return <Working width={18} />;
            case 'CALC_RES_OK_WITH_ERRORS':
            case 'CALC_RES_ERROR':
                return <Close size="medium" />;
            case 'CALC_DISABLED':
                return <Cached size="medium" />;
            default:
                return <Check size="medium" />;
        }
    };

    const getLabel = () => {
        switch (ahuCalculationStatus) {
            case 'CALC_RES_OK':
            case 'CALC_RES_OK_WITH_WARNINGS':
            case 'CALC_RES_OK_WITH_ERRORS':
                return l(`ahuDesign.calcButtonCalculated`).toUpperCase();
            case 'CALC_RES_ERROR':
                return l(`ahuDesign.calcButtonError`).toUpperCase();
            case 'CALC_RUNNING':
                return `${l(`ahuDesign.calcButtonCalculating`)}...`.toUpperCase();
            case 'CALC_NEEDS_REFRESH':
            case 'CALC_DISABLED':
            default:
                return l(`ahuDesign.calcButtonNeedRefresh`).toUpperCase();
        }
    };

    return (
        <CalculationEventWrapper
            ahuCalculationStatus={ahuCalculationStatus}
            eventStore={eventStore}
            onCalculationResultChanged={onCalculationResultChanged}
        >
            <div
                title={
                    shouldDisableButton
                        ? l('ahuDesignLite.airflowNotInRange')
                        : undefined
                }
            >
                <Button
                    id={'calculate-button'}
                    buttonType="contained"
                    label={getLabel()}
                    disabled={shouldDisableButton}
                    onClick={handleCalculationClick}
                    buttonColor={getColor()}
                    icon={getIcon}
                    data-transaction-name="calculate-lite"
                    aria-label={getLabel()}
                />
            </div>
        </CalculationEventWrapper>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export default withTransaction('CalculateButton', 'component')(CalculateButton);
